import React from 'react';

import { forkEntry, isLocked, versionKey, type AdVersionLike } from '../ad-versions';

/**
 * The version lifecycle of an ad entity (banner kit, mini-game), the video rule: a version LOCKS once generated,
 * and editing a locked version first FORKS a new unlocked one (selected at once), so a generated result is never
 * mutated in place. A Generate fills the selected version when it is still unlocked, else a new placeholder
 * version. Forks are minted with a client timestamp (their identity), shown optimistically through `patchEntity`
 * and persisted through `persistFork`; the backend creates a missing target itself, so the persist only matters
 * for what the fork CARRIES (a redraw needs the copied hero to be there before the run starts).
 */

/** A version with its stable key ({@link versionKey} of `created_at`). */
export type KeyedVersion<V extends AdVersionLike> = V & { key: string };

/** A version's content without its identity or lock (what an upsert's `ad_version` carries). */
export type AdVersionEntry<V extends AdVersionLike> = Omit<V, 'created_at' | 'locked'>;

/** A minted (or reused) version: its RFC 3339 `created_at`, its key, and whether it is saved. */
export interface MintedVersion {
  at: string;
  key: string;
  /** Resolves true once the new version is saved (at once for a reused one), false when the save failed. */
  persisted: Promise<boolean>;
}

export interface UseAdVersionsArgs<V extends AdVersionLike> {
  /** The entity's versions, newest first (the backend's order). */
  versions: V[];
  entityId?: string;
  tenantId?: number;
  /** Save a new unlocked version keyed by `at`. */
  persistFork: (entry: AdVersionEntry<V>, at: string) => Promise<unknown>;
  /** Show a new version list at once (the library's optimistic patch). */
  patchEntity: (versions: V[]) => void;
  /** A fork could not be saved: it was taken back off the list. When it was selected, `restored` (the version it came
   *  from, else the newest) is selected again, so the editor shows its document again; `noneLeft`: the entity has no
   *  version any more. */
  onForkLost?: (restored: KeyedVersion<V> | undefined, noneLeft: boolean) => void;
}

export interface UseAdVersions<V extends AdVersionLike> {
  versions: Array<KeyedVersion<V>>;
  /** The selected version (the newest when none was picked); undefined when the entity has none. */
  selected?: KeyedVersion<V>;
  selectedKey?: string;
  select: (key: string) => void;
  /** The selected version as of NOW, a fork made earlier in the same event included (the render-time `selected`
   *  lags until the next render). */
  current: () => KeyedVersion<V> | undefined;
  /** Always start a new unlocked version from `entry` and select it. Null without an entity. */
  fork: (entry: AdVersionEntry<V>) => MintedVersion | null;
  /** Fork the selected version when it is locked (from `entry`, default: a copy of it); null when there is nothing
   *  to fork (no version, or the selected one is still unlocked and takes the edit in place). */
  forkIfLocked: (entry?: AdVersionEntry<V>) => MintedVersion | null;
  /** The version a run fills: the selected one while unlocked (its `persisted` waits for a fork of this session to
   *  be saved), else a new version from `placeholder`. */
  mintTarget: (placeholder: AdVersionEntry<V>) => MintedVersion | null;
  /** Show a draft's (unlocked version's) current document on its entry at once: a draft owns its document, every
   *  edit made on it lands there (and is saved there by the caller). A locked version is never changed. */
  patchVersion: (key: string, params: VersionParams<V>) => void;
  /** Whether the version keyed `key` is saved: waits for a fork of this session, true at once for any other. */
  persistedOf: (key: string) => Promise<boolean>;
}

/** The document a version carries (`params`), for a version type that has one. */
export type VersionParams<V> = V extends { params: infer P } ? P : never;

/* ---------- pure helpers ---------- */

/** Each version with its key, order kept. Pure. */
export const keyVersions = <V extends AdVersionLike>(versions: readonly V[]): Array<KeyedVersion<V>> =>
  versions.map((v) => ({ ...v, key: versionKey(v.created_at) }));

/** The version whose key is `key`, else the first (newest); undefined for an empty list. Pure. */
export const resolveSelected = <V extends AdVersionLike>(
  versions: ReadonlyArray<KeyedVersion<V>>,
  key: string | undefined,
): KeyedVersion<V> | undefined => (key ? versions.find((v) => v.key === key) : undefined) ?? versions[0];

/** A fresh version timestamp (RFC 3339) at `nowMs`, bumped past any existing version's time so two forks minted in
 *  the same millisecond never share an identity. Pure. */
export const mintVersionAt = (nowMs: number, existing: readonly AdVersionLike[]): string => {
  let ms = nowMs;
  const taken = new Set(existing.map((v) => versionKey(v.created_at)));
  while (taken.has(String(ms))) ms += 1;
  return new Date(ms).toISOString();
};

/** A new unlocked version from its content, keyed `at`. Pure. */
export const materializeVersion = <V extends AdVersionLike>(entry: AdVersionEntry<V>, at: string): V =>
  ({ ...entry, created_at: at, locked: false }) as unknown as V;

/** Where a Generate writes: into the selected version while it is unlocked (`reuse`), else a new one. Pure. */
export const mintTargetPlan = <V extends AdVersionLike>(
  selected: KeyedVersion<V> | undefined,
): { reuse: KeyedVersion<V> } | { fork: true } =>
  selected && !isLocked(selected) ? { reuse: selected } : { fork: true };

/** `versions` without the one keyed `key` (the same array when none carries it). Pure. */
export const withoutVersion = <V extends AdVersionLike>(versions: V[], key: string): V[] => {
  const left = versions.filter((v) => versionKey(v.created_at) !== key);
  return left.length === versions.length ? versions : left;
};

/** `versions` with the draft (unlocked) keyed `key` carrying `params`; the same array when no draft carries the key
 *  (a locked version is never changed). Pure. */
export const withVersionParams = <V extends AdVersionLike>(
  versions: V[],
  key: string,
  params: VersionParams<V>,
): V[] => {
  const at = versions.findIndex((v) => versionKey(v.created_at) === key);
  if (at < 0 || isLocked(versions[at])) return versions;
  const next = versions.slice();
  next[at] = { ...versions[at], params };
  return next;
};

/* ---------- the hook ---------- */

export const useAdVersions = <V extends AdVersionLike>(args: UseAdVersionsArgs<V>): UseAdVersions<V> => {
  const { versions, entityId, tenantId } = args;
  const [selectedKey, setSelectedKey] = React.useState<string | undefined>(undefined);

  const keyed = React.useMemo(() => keyVersions(versions), [versions]);
  const selected = resolveSelected(keyed, selectedKey);

  // The freshest list and selection, for handlers that run twice before a re-render (a fork followed at once by an
  // edit must see the fork, not fork again). Synced from props on every render, bumped by each fork.
  const latestRef = React.useRef<V[]>(versions);
  latestRef.current = versions;
  const selectedRef = React.useRef<KeyedVersion<V> | undefined>(selected);
  selectedRef.current = selected;
  // The callbacks through refs: callers pass inline closures, and a fork must use the current ones.
  const persistRef = React.useRef(args.persistFork);
  persistRef.current = args.persistFork;
  const patchRef = React.useRef(args.patchEntity);
  patchRef.current = args.patchEntity;
  const lostRef = React.useRef(args.onForkLost);
  lostRef.current = args.onForkLost;
  // Each fork's save, by key: a Generate that reuses a just-forked version waits for it (the run reads the version
  // the backend has, and a hero still in flight would be charged and painted again).
  const persistsRef = React.useRef(new Map<string, Promise<boolean>>());

  const select = React.useCallback((key: string): void => setSelectedKey(key), []);
  const current = React.useCallback((): KeyedVersion<V> | undefined => selectedRef.current, []);

  const fork = React.useCallback(
    (entry: AdVersionEntry<V>): MintedVersion | null => {
      if (!entityId) return null;
      const at = mintVersionAt(Date.now(), latestRef.current);
      const key = versionKey(at);
      const source = selectedRef.current?.key;
      const version = materializeVersion<V>(entry, at);
      const next = [version, ...latestRef.current];
      latestRef.current = next;
      selectedRef.current = { ...version, key };
      setSelectedKey(key);
      patchRef.current(next);
      const persisted: Promise<boolean> = tenantId
        ? persistRef.current(entry, at).then(
            () => true,
            () => false,
          )
        : Promise.resolve(false);
      persistsRef.current.set(key, persisted);
      // A fork the backend does not have is taken back: a run into it would paint (and charge) art the editor shows
      // as carried, and every later save of it would be lost.
      void persisted.then((ok) => {
        if (ok || !tenantId) return;
        const left = withoutVersion(latestRef.current, key);
        if (left.length === latestRef.current.length) return;
        // Its failed save stays on record: anything still waiting on it (a preview, an export) reads false.
        latestRef.current = left;
        const lostSelected = selectedRef.current?.key === key;
        if (lostSelected) {
          const back = resolveSelected(keyVersions(left), source);
          selectedRef.current = back;
          setSelectedKey(back?.key);
        }
        patchRef.current(left);
        lostRef.current?.(lostSelected ? selectedRef.current : undefined, left.length === 0);
      });
      return { at, key, persisted };
    },
    [entityId, tenantId],
  );

  const forkIfLocked = React.useCallback(
    (entry?: AdVersionEntry<V>): MintedVersion | null => {
      const current = selectedRef.current;
      if (!current || !isLocked(current)) return null;
      if (entry) return fork(entry);
      // Copy the stored version (not the keyed view, whose `key` must not reach the backend).
      const source = latestRef.current.find((v) => versionKey(v.created_at) === current.key);
      return source ? fork(forkEntry(source)) : null;
    },
    [fork],
  );

  const mintTarget = React.useCallback(
    (placeholder: AdVersionEntry<V>): MintedVersion | null => {
      if (!entityId) return null;
      const plan = mintTargetPlan(selectedRef.current);
      if ('reuse' in plan) {
        const persisted = persistsRef.current.get(plan.reuse.key) ?? Promise.resolve(true);
        return { at: plan.reuse.created_at, key: plan.reuse.key, persisted };
      }
      return fork(placeholder);
    },
    [entityId, fork],
  );

  const patchVersion = React.useCallback((key: string, params: VersionParams<V>): void => {
    const next = withVersionParams(latestRef.current, key, params);
    if (next === latestRef.current) return;
    latestRef.current = next;
    if (selectedRef.current?.key === key) selectedRef.current = { ...selectedRef.current, params };
    patchRef.current(next);
  }, []);

  const persistedOf = React.useCallback(
    (key: string): Promise<boolean> => persistsRef.current.get(key) ?? Promise.resolve(true),
    [],
  );

  return {
    versions: keyed,
    selected,
    selectedKey: selected?.key,
    select,
    current,
    fork,
    forkIfLocked,
    mintTarget,
    patchVersion,
    persistedOf,
  };
};

export default useAdVersions;
